import React, { useEffect, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import { AreaData } from '../api/api';
import { renderAreas } from '../util/areas';
import { getMarkerProps } from '../util/marker';

interface DropPinMapProps {
    fixed?: boolean;
    onCoordinatesChange: (coordinates: { lat: number; lng: number }) => void; // Callback to send coordinates back to parent
    location: { lat: number; lng: number } | null;
    markerData: Parameters<typeof getMarkerProps>[0];
    allAreas: AreaData[];
}

const MapSinglePoint: React.FC<DropPinMapProps> = ({ fixed, onCoordinatesChange, markerData, location, allAreas  }) => {
    const mapContainerRef = useRef<HTMLDivElement | null>(null); // Ref to the map container
    const mapRef = useRef<maplibregl.Map | null>(null); // Ref to the map instance
    const markerRef = useRef<maplibregl.Marker | null>(null); // Ref to the marker instance
    const [loadingState, setLoadingState] = useState<null | 'initiated' | 'loaded'>(null); // Loading state
    
    !fixed && useEffect(() => {
        // Use Geolocation API to get user's location
        navigator.geolocation.getCurrentPosition(
            (position) => {
                const { latitude, longitude } = position.coords;
                onCoordinatesChange({ lat: latitude, lng: longitude }); // Send initial coordinates to parent
                console.log('Initial coordinates:', { lat: latitude, lng: longitude }); // Output to console
            },
            (error) => {
                console.error('Geolocation error:', error);
                // Fallback to Edinburgh if geolocation fails
                const fallbackCoordinates = { lat: 55.9533, lng: -3.1883 };
                onCoordinatesChange(fallbackCoordinates); // Send fallback coordinates to parent
                console.log('Using fallback coordinates:', fallbackCoordinates); // Output to console
            }
        );
    }, []);


    // render map once the location and mapContainerRef are loaded. Destroy map at exit
    useEffect(() => {        
        if (!mapContainerRef.current || mapRef.current || !location?.lat || loadingState !== null) {
            return;
        }

        setLoadingState('initiated');
        mapRef.current = new maplibregl.Map({
            container: mapContainerRef.current!,
            style: 'https://tiles.openfreemap.org/styles/liberty',
            center: [location.lng, location.lat], // Set center to current location
            zoom: 15,
            attributionControl: false
        });
        mapRef.current.on('load', () => setLoadingState('loaded')); // Hide spinner when map is ready
    }, [!!location?.lat, mapContainerRef]);

    
    useEffect(() => {
        // Cleanup function to remove the map instance on unmount.
        // There is some kind of issue./bug with the parent component being unmounted and remounted in an unexpected way on page load,
        // but by resetting the mapRef and loadingState to null here it seems to be ok (i would consider this a workaround rather than a proper fix though). 
        return () => {
            mapRef.current?.remove();
            mapRef.current = null;
            setLoadingState(null)
        };
    }, []);   

    // render marker, once map and markerData are loaded
    useEffect(() => {
        markerRef.current?.remove();

        if (!mapRef.current || !location || loadingState !== 'loaded') {
            return;
        }

        const marker = new maplibregl.Marker({ draggable: !fixed, ...getMarkerProps(markerData, 'gender') })
            .setLngLat([location.lng, location.lat]) // Set marker to current location
            .addTo(mapRef.current);

        markerRef.current = marker; // Store marker reference

        // Listen for the dragend event to update coordinates and center the map
        !fixed && marker.on('dragend', () => {
            const lngLat = marker.getLngLat();
            const newCoordinates = { lat: lngLat.lat, lng: lngLat.lng };


            // Center the map on the dragged marker
            mapRef.current?.flyTo({
                center: [lngLat.lng, lngLat.lat], // Set new center
                zoom: 15, // Keep the same zoom level
                speed: 1.2, // Animation speed
                curve: 1, // Smoothing curve
                essential: true, // For accessibility
            });

            // Send updated coordinates to parent
            onCoordinatesChange(newCoordinates);

            console.log('Coordinates changed:', newCoordinates); // Output to console
        });
    }, [loadingState, markerData]);

    // render areas, once map and areas are laoded
    useEffect(() => {
        loadingState == 'loaded' && renderAreas(allAreas, true, mapRef.current as maplibregl.Map)
    }, [loadingState, allAreas]);

    return (
        <div style={{ position: 'relative', width: '100%', height: '350px' }}>
            {loadingState !== 'loaded' && (
                <div
                    style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 1000,
                    }}
                >
                    <div className="spinner" />
                </div>
            )}
            <div ref={mapContainerRef} style={{ width: '100%', height: '100%' }}></div>

            {/* CSS for the spinner */}
            <style>{`
                .spinner {
                    border: 4px solid rgba(0, 0, 0, 0.1);
                    border-top: 4px solid #007bff;
                    border-radius: 50%;
                    width: 40px;
                    height: 40px;
                    animation: spin 0.8s linear infinite;
                }
                @keyframes spin {
                    0% {
                        transform: rotate(0deg);
                    }
                    100% {
                        transform: rotate(360deg);
                    }
                }
            `}</style>
        </div>
    );
};

export default MapSinglePoint;
